// DeliveryForm.jsx - Complete, Verified Production File
import React, { useState, useEffect, useRef } from "react";
import { apiClient } from "../apiClient";

export default function DeliveryForm({ onPlanCreated }) {
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [products, setProducts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const [deliveries, setDeliveries] = useState([
    {
      clientId: "",
      clientSearch: "",
      showDropdown: false,
      others: "",
      items: [{ productId: "", quantity: 1, pricePerUnit: 0 }]
    }
  ]);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    driverId: "",
    vehicleNumber: ""
  });
  const [driverSearch, setDriverSearch] = useState("");
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const driverDropdownRef = useRef(null);

  // Click-outside handler to close dropdowns safely
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (driverDropdownRef.current && !driverDropdownRef.current.contains(e.target)) {
        setShowDriverDropdown(false);
      }

      const clickedInsideClientDropdown = e.target.closest('.client-dropdown-container');
      if (!clickedInsideClientDropdown) {
        setDeliveries(prev => prev.map(d => d.showDropdown ? { ...d, showDropdown: false } : d));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch initial lookups with unmount cleanup guard
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setInitialLoading(true);
      try {
        const [clientsData, driversData, productsData] = await Promise.all([
          apiClient.get("/clients/"),
          apiClient.get("/auth/users"),
          apiClient.get("/products/")
        ]);

        if (!isMounted) return;

        setClients(clientsData || []);
        setDrivers((driversData || []).filter(user => user.role === "driver"));
        setProducts(productsData || []);

        if (productsData && productsData.length > 0) {
          setDeliveries(prev => {
            const hasUserInput = prev.some(d => d.clientId || d.others || d.items.some(i => i.quantity !== 1));
            if (hasUserInput) return prev;
            return [{
              clientId: "",
              clientSearch: "",
              showDropdown: false,
              others: "",
              items: [{
                productId: productsData[0].id,
                quantity: 1,
                pricePerUnit: (productsData[0].price_groszy || 0) / 100
              }]
            }];
          });
        }
      } catch (err) {
        if (isMounted) {
          setMessage("❌ Failed to load data: " + (err.message || "Check console"));
        }
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, []);

  const handleItemChange = (deliveryIndex, itemIndex, field, value) => {
    setDeliveries(prev => prev.map((delivery, dIdx) => {
      if (dIdx !== deliveryIndex) return delivery;

      const newItems = delivery.items.map((item, iIdx) => {
        if (iIdx !== itemIndex) return item;

        const updated = { ...item, [field]: value };
        if (field === "productId") {
          const product = products.find(p => p.id === value);
          if (product) {
            updated.pricePerUnit = (product.price_groszy || 0) / 100;
          }
        }
        return updated;
      });
      return { ...delivery, items: newItems };
    }));
  };

  const addItem = (deliveryIndex) => {
    const defaultProduct = products[0] || { id: "", price_groszy: 0 };
    setDeliveries(prev => prev.map((delivery, idx) => {
      if (idx !== deliveryIndex) return delivery;
      return {
        ...delivery,
        items: [...delivery.items, {
          productId: defaultProduct.id,
          quantity: 1,
          pricePerUnit: (defaultProduct.price_groszy || 0) / 100
        }]
      };
    }));
  };

  const removeItem = (deliveryIndex, itemIndex) => {
    setDeliveries(prev => prev.map((delivery, dIdx) => {
      if (dIdx !== deliveryIndex) return delivery;
      if (delivery.items.length <= 1) return delivery;
      return {
        ...delivery,
        items: delivery.items.filter((_, iIdx) => iIdx !== itemIndex)
      };
    }));
  };

  const addDelivery = () => {
    const defaultProduct = products[0] || { id: "", price_groszy: 0 };
    setDeliveries(prev => [
      ...prev,
      {
        clientId: "",
        clientSearch: "",
        showDropdown: false,
        others: "",
        items: [{
          productId: defaultProduct.id,
          quantity: 1,
          pricePerUnit: (defaultProduct.price_groszy || 0) / 100
        }]
      }
    ]);
  };

  const removeDelivery = (deliveryIndex) => {
    if (deliveries.length > 1) {
      const delivery = deliveries[deliveryIndex];
      const hasData = delivery.clientId ||
        delivery.others ||
        delivery.items.some(i => i.productId && i.quantity >= 1);

      if (hasData) {
        const confirmed = window.confirm('Remove this client delivery? All entered data will be lost.');
        if (!confirmed) return;
      }

      setDeliveries(prev => prev.filter((_, i) => i !== deliveryIndex));
    }
  };

  const handleOthersChange = (deliveryIndex, value) => {
    setDeliveries(prev => prev.map((d, i) =>
      i === deliveryIndex ? { ...d, others: value } : d
    ));
  };

  const getDeliveryTotal = (delivery) => {
    return delivery.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.pricePerUnit) || 0;
      return sum + (qty * rate);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.driverId) {
      setMessage("❌ Please select a driver");
      return;
    }

    const missingClient = deliveries.find(d => !d.clientId);
    if (missingClient) {
      setMessage("❌ Please select a client for all deliveries");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const requestPayloads = deliveries.map(delivery => {
        const deliveryItems = delivery.items.map(item => {
          const product = products.find(p => p.id === item.productId);
          const validatedQty = Math.max(1, parseInt(item.quantity) || 1);
          const unitPrice = Math.round((parseFloat(item.pricePerUnit) || 0) * 100);
          const totalValue = validatedQty * unitPrice;

          return {
            delivery_plan_id: 0,
            client_id: parseInt(delivery.clientId),
            item_name: product ? `${product.name}${product.size ? ' ' + product.size : ''}` : "Unknown Item",
            quantity: validatedQty,
            unit_price_groszy: unitPrice,
            total_value_groszy: totalValue,
            payment_status: "Not Paid",
            payment_amount_groszy: null,
            signature: null
          };
        });

        return {
          assigned_user_id: parseInt(formData.driverId),
          date: formData.date,
          vehicle_number: (formData.vehicleNumber || "").trim() || null,
          others: (delivery.others || "").trim() || null,
          items: deliveryItems
        };
      });

      await Promise.all(
        requestPayloads.map(payload => apiClient.post("/delivery-plans/", payload))
      );

      setMessage(`✅ Created ${deliveries.length} delivery plan(s) successfully!`);

      setFormData({
        date: new Date().toISOString().split("T")[0],
        driverId: "",
        vehicleNumber: ""
      });
      setDriverSearch("");
      const defaultProduct = products[0] || { id: "", price_groszy: 0 };
      setDeliveries([{
        clientId: "",
        clientSearch: "",
        showDropdown: false,
        others: "",
        items: [{
          productId: defaultProduct.id,
          quantity: 1,
          pricePerUnit: (defaultProduct.price_groszy || 0) / 100
        }]
      }]);

      if (onPlanCreated) onPlanCreated();
    } catch (err) {
      setMessage(`❌ Base failure: ${err.message || "Failed to submit operations"}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredDrivers = drivers.filter(driver =>
    driver.username && driver.username.toLowerCase().includes(driverSearch.toLowerCase())
  );

  const selectedDriver = drivers.find(d => d.id === parseInt(formData.driverId));

  if (initialLoading) {
    return (
      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F76B4C]"></div>
          <span className="ml-3 text-[#2D2F5E] font-medium">Fetching dependencies...</span>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
      <h2 className="text-lg font-bold text-[#2D2F5E] mb-4">📦 Create Delivery Plans (Bulk)</h2>

      {message && (
        <div className={`mb-4 p-2.5 rounded text-sm font-medium ${message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#2D2F5E] mb-1">Date</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white focus:outline-none focus:ring-2 focus:ring-[#F76B4C]"
          />
        </div>

        {/* Driver Autocomplete */}
        <div className="relative" ref={driverDropdownRef}>
          <label className="block text-sm font-medium text-[#2D2F5E] mb-1">Driver</label>
          <input
            type="text"
            placeholder={selectedDriver ? selectedDriver.username : "Search driver..."}
            value={driverSearch}
            onChange={(e) => {
              setDriverSearch(e.target.value);
              setShowDriverDropdown(true);
            }}
            onFocus={() => setShowDriverDropdown(true)}
            className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white focus:outline-none focus:ring-2 focus:ring-[#F76B4C]"
          />
          {showDriverDropdown && filteredDrivers.length > 0 && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-[#2D2F5E] border-opacity-30 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredDrivers.map((driver) => (
                <div
                  key={driver.id}
                  onClick={() => {
                    setFormData({ ...formData, driverId: driver.id });
                    setDriverSearch("");
                    setShowDriverDropdown(false);
                  }}
                  className="p-2 hover:bg-[#F5E9D8] cursor-pointer text-[#2D2F5E] transition text-sm"
                >
                  {driver.username}
                </div>
              ))}
            </div>
          )}
          {selectedDriver && (
            <div className="mt-1.5 text-xs text-[#2D2F5E] bg-[#F5E9D8] bg-opacity-40 p-1.5 rounded inline-flex items-center">
              Selected Driver: <span className="font-bold ml-1">{selectedDriver.username}</span>
              <button
                type="button"
                onClick={() => {
                  setFormData({ ...formData, driverId: "" });
                  setDriverSearch("");
                }}
                className="ml-2 text-[#F76B4C] hover:text-[#E65A3C] font-bold"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {/* Vehicle Number */}
        <div>
          <label className="block text-sm font-medium text-[#2D2F5E] mb-1">Vehicle Number (Optional)</label>
          <input
            type="text"
            placeholder="e.g., GDA-1234"
            value={formData.vehicleNumber}
            onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
            className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white focus:outline-none focus:ring-2 focus:ring-[#F76B4C]"
          />
        </div>

        {/* Multiple Client Deliveries */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-[#2D2F5E] -mb-2">Client Deliveries Configuration</label>

          {deliveries.map((delivery, deliveryIndex) => {
            const filteredClients = clients.filter(client =>
              client.name && client.name.toLowerCase().includes(delivery.clientSearch.toLowerCase())
            );
            const selectedClient = clients.find(c => c.id === parseInt(delivery.clientId));
            const deliveryTotal = getDeliveryTotal(delivery);

            return (
              <div key={deliveryIndex} className="p-4 border border-[#2D2F5E] border-opacity-20 rounded-xl bg-[#F5E9D8] bg-opacity-40 shadow-inner">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="font-bold text-sm text-[#2D2F5E]">Client Entry #{deliveryIndex + 1}</h3>
                    {deliveryTotal > 0 && (
                      <p className="text-xs text-[#2D2F5E] mt-0.5">
                        Client Subtotal: <span className="font-bold text-[#F76B4C]">{deliveryTotal.toFixed(2)} PLN</span>
                      </p>
                    )}
                  </div>
                  {deliveries.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDelivery(deliveryIndex)}
                      className="text-red-600 hover:text-red-800 text-xs font-semibold"
                    >
                      Remove Destination
                    </button>
                  )}
                </div>

                {/* Client Autocomplete Selection */}
                <div className="relative mb-3 client-dropdown-container">
                  <label className="block text-xs font-semibold text-[#2D2F5E] mb-1">Select Client Target</label>
                  <input
                    type="text"
                    placeholder={selectedClient ? `${selectedClient.name}${selectedClient.address ? ` – ${selectedClient.address}` : ''}` : "Type to filter down clients..."}
                    value={delivery.clientSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDeliveries(prev => prev.map((d, i) =>
                        i === deliveryIndex ? { ...d, clientSearch: val, showDropdown: true } : d
                      ));
                    }}
                    onFocus={() => {
                      setDeliveries(prev => prev.map((d, i) =>
                        i === deliveryIndex ? { ...d, showDropdown: true } : d
                      ));
                    }}
                    className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#F76B4C]"
                  />
                  {delivery.showDropdown && filteredClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#2D2F5E] border-opacity-30 rounded-lg shadow-lg max-h-44 overflow-y-auto">
                      {filteredClients.map((client) => (
                        <div
                          key={client.id}
                          onClick={() => {
                            setDeliveries(prev => prev.map((d, i) =>
                              i === deliveryIndex ? { ...d, clientId: client.id, clientSearch: "", showDropdown: false } : d
                            ));
                          }}
                          className="p-2 hover:bg-[#F5E9D8] cursor-pointer text-[#2D2F5E] transition text-xs"
                        >
                          <div className="font-semibold">{client.name}</div>
                          {client.address && <div className="text-[10px] opacity-70 mt-0.5">{client.address}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Selected client layout with restored clear action */}
                  {selectedClient && (
                    <div className="mt-1 text-xs text-[#2D2F5E] font-medium inline-flex items-center gap-1.5 bg-white bg-opacity-60 px-2 py-1 rounded border border-[#2D2F5E] border-opacity-10">
                      <span>✓ Attached: <span className="text-[#F76B4C] font-semibold">{selectedClient.name}</span></span>
                      {selectedClient.address && <span className="opacity-70 text-[11px]"> ({selectedClient.address})</span>}
                      <button
                        type="button"
                        onClick={() => {
                          setDeliveries(prev => prev.map((d, i) =>
                            i === deliveryIndex ? { ...d, clientId: "", clientSearch: "" } : d
                          ));
                        }}
                        className="text-[#F76B4C] hover:text-[#E65A3C] font-bold ml-1 px-1 transition"
                        title="Clear selected client"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>

                {/* Optional Custom Items */}
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-[#2D2F5E] mb-1">Non-Inventory Custom Goods / Notes (Optional)</label>
                  <textarea
                    placeholder="e.g., Return empty trays, deliver to side door..."
                    value={delivery.others || ""}
                    onChange={(e) => handleOthersChange(deliveryIndex, e.target.value)}
                    rows="1"
                    className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#F76B4C]"
                  />
                </div>

                {/* Items collection mapping */}
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-[#2D2F5E]">Catalog Products</label>
                  {delivery.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center flex-wrap gap-2 p-1.5 border border-[#2D2F5E] border-opacity-10 rounded bg-white">
                      <select
                        value={item.productId}
                        onChange={(e) => handleItemChange(deliveryIndex, itemIndex, "productId", parseInt(e.target.value) || "")}
                        className="flex-1 min-w-[110px] p-1 border border-[#2D2F5E] border-opacity-20 rounded text-[#2D2F5E] bg-white text-xs"
                      >
                        <option value="" disabled>Choose Product</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} {p.size ? `(${p.size})` : ''}
                          </option>
                        ))}
                      </select>

                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleItemChange(deliveryIndex, itemIndex, "quantity", val === "" ? "" : Math.max(1, parseInt(val) || 1));
                        }}
                        className="w-14 p-1 border border-[#2D2F5E] border-opacity-20 rounded text-[#2D2F5E] bg-white text-xs text-center"
                        placeholder="Qty"
                      />

                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.pricePerUnit}
                        onChange={(e) => handleItemChange(deliveryIndex, itemIndex, "pricePerUnit", e.target.value === "" ? "" : parseFloat(e.target.value) || 0)}
                        className="w-16 p-1 border border-[#2D2F5E] border-opacity-20 rounded text-[#2D2F5E] bg-white text-xs text-right"
                        placeholder="Price"
                      />

                      <button
                        type="button"
                        onClick={() => removeItem(deliveryIndex, itemIndex)}
                        className="text-red-500 hover:text-red-700 font-bold text-xs px-1 disabled:opacity-30"
                        disabled={delivery.items.length <= 1}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addItem(deliveryIndex)}
                    className="inline-flex items-center text-[#2D2F5E] hover:text-[#F76B4C] text-xs font-bold mt-1"
                  >
                    + Add Another Product Row
                  </button>
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={addDelivery}
            className="w-full bg-[#2D2F5E] hover:bg-[#1D1F4E] text-white px-4 py-2 rounded-xl text-sm transition font-medium shadow-sm"
          >
            + Add Another Client Drop Point
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-3 rounded-xl transition shadow disabled:opacity-60 font-bold text-sm tracking-wide mt-4"
        >
          {loading ? "Processing Network Submissions..." : `Save changes & Compile ${deliveries.length} Route Plan(s)`}
        </button>
      </div>
    </form>
  );
}